package org.teddapp.project.ui.settings

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import org.teddapp.project.server.ServerAssistantModel
import org.teddapp.project.server.ServerException
import org.teddapp.project.server.ServerSession
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddFilterChip
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.teddPasswordTransformation

/** Un service de modèle proposé tout fait : sa base d'API et ce qu'il attend. */
internal data class ModelService(
    val id: String,
    val label: String,
    /** La base de l'API jusqu'au segment de version ; le RAG ajoute `/chat/completions`. */
    val url: String,
    val kind: String,
    val needsKey: Boolean,
    /** Un nom de modèle qui marche chez ce service, proposé en exemple. */
    val example: String,
    val window: Int,
)

/** La logique du formulaire, sans Compose : testable. */
internal object AssistantModelForm {
    const val WINDOW_MIN = 2_048
    const val WINDOW_MAX = 131_072

    val services: List<ModelService> = listOf(
        ModelService("mistral", "Mistral", "https://api.mistral.ai/v1", "openai", true, "mistral-small-latest", 32_768),
        ModelService("openai", "OpenAI", "https://api.openai.com/v1", "openai", true, "gpt-4o-mini", 32_768),
        ModelService("gemini", "Gemini", "https://generativelanguage.googleapis.com/v1beta/openai", "openai", true, "gemini-2.0-flash", 32_768),
        ModelService("groq", "Groq", "https://api.groq.com/openai/v1", "openai", true, "llama-3.3-70b-versatile", 32_768),
        ModelService("llama", "llama-server", "", "llama", false, "phi4-mini", 8_192),
        ModelService("ollama", "Ollama", "", "openai", false, "qwen2.5:7b", 8_192),
        ModelService("other", "Autre", "", "openai", false, "", 8_192),
    )

    /** Le service dont l'adresse enregistrée est la base, sinon « Autre » ou un serveur local selon le genre. */
    fun serviceFor(url: String, kind: String): ModelService =
        services.firstOrNull { it.url.isNotEmpty() && it.url == url.trimEnd('/') }
            ?: if (kind == "llama") services.first { it.id == "llama" } else services.first { it.id == "other" }

    private fun hostOf(url: String): String =
        url.trim().substringAfter("://", "").substringBefore('/').substringBefore(':').trim('[', ']').lowercase()

    /**
     * Vrai si l'adresse désigne l'appareil ou un réseau local : le serveur ne la
     * joindra pas, sauf s'il l'a mise dans ses hôtes admis (développement, tunnel).
     * Un avertissement, pas un refus : c'est le serveur qui tranche.
     */
    fun looksLocal(url: String): Boolean {
        val host = hostOf(url)
        return host == "localhost" || host == "127.0.0.1" || host == "::1" || host.endsWith(".local") ||
            host.startsWith("10.") || host.startsWith("192.168.") ||
            Regex("^172\\.(1[6-9]|2[0-9]|3[01])\\.").containsMatchIn(host)
    }

    /** Ce qui ne va pas dans le formulaire, avant même d'appeler le serveur ; `null` si tout va. */
    fun check(url: String, name: String, window: String): String? {
        val u = url.trim()
        return when {
            u.isEmpty() -> "L'adresse du service manque."
            !(u.startsWith("http://") || u.startsWith("https://")) -> "L'adresse doit commencer par http:// ou https://."
            hostOf(u).isEmpty() -> "L'adresse n'a pas de nom d'hôte."
            name.isBlank() -> "Le nom du modèle manque."
            window.trim().toIntOrNull()?.let { it in WINDOW_MIN..WINDOW_MAX } != true ->
                "La fenêtre de contexte doit être un nombre entre $WINDOW_MIN et $WINDOW_MAX."
            else -> null
        }
    }

    /** Ce que dit le bouton « Tester », d'après la réponse du serveur. */
    fun testMessage(ok: Boolean, latencyMs: Long?, code: String?): String = when {
        ok -> "Le modèle répond" + (latencyMs?.let { " en ${formatLatency(it)}" } ?: "") + "."
        else -> when (code) {
            "model_unauthorized" -> "Le service refuse la clé."
            "model_quota" -> "Le quota du service est atteint."
            "model_not_found" -> "Le service ne connaît pas ce modèle : vérifie son nom."
            "model_blocked" -> "L'adresse n'est pas joignable depuis le serveur."
            "model_unavailable" -> "Le service ne répond pas."
            "rag_unavailable" -> "L'assistant est indisponible pour le moment."
            else -> "Le modèle a répondu une erreur."
        }
    }

    private fun formatLatency(ms: Long): String =
        if (ms < 1000) "$ms ms" else "${kotlin.math.round(ms / 100.0).toLong() / 10.0} s"
}

/** Réglages : le modèle de langue de l'assistant, chez le serveur. */
@Composable
internal fun AssistantModelSection(session: ServerSession) {
    val scope = rememberCoroutineScope()
    var loaded by remember { mutableStateOf(false) }
    var current by remember { mutableStateOf<ServerAssistantModel?>(null) }
    var service by remember { mutableStateOf(AssistantModelForm.services.first()) }
    var url by remember { mutableStateOf(service.url) }
    var name by remember { mutableStateOf("") }
    var key by remember { mutableStateOf("") }
    var window by remember { mutableStateOf(service.window.toString()) }
    var busy by remember { mutableStateOf(false) }
    var status by remember { mutableStateOf<String?>(null) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(session.connected) {
        if (!session.connected) return@LaunchedEffect
        runCatching { session.authed { api, access -> api.assistantModel(access) } }
            .onSuccess { found ->
                current = found
                if (found != null) {
                    service = AssistantModelForm.serviceFor(found.url, found.kind)
                    url = found.url; name = found.name; window = found.window.toString()
                }
            }
            .onFailure { e -> error = (e as? ServerException)?.let(::serverMessage) ?: "Le serveur est injoignable." }
        loaded = true
    }

    fun choose(s: ModelService) {
        service = s
        if (s.url.isNotEmpty()) url = s.url
        else if (AssistantModelForm.services.any { it.url == url }) url = ""
        if (name.isBlank() || AssistantModelForm.services.any { it.example == name }) name = s.example
        window = s.window.toString()
        status = null; error = null
    }

    suspend fun save(): Boolean {
        val problem = AssistantModelForm.check(url, name, window)
        if (problem != null) { error = problem; return false }
        return try {
            current = session.authed { api, access ->
                api.saveAssistantModel(access, url.trim().trimEnd('/'), name.trim(), service.kind, window.trim().toInt(), key.ifEmpty { null })
            }
            key = ""
            error = null
            true
        } catch (e: ServerException) {
            error = serverMessage(e); false
        } catch (e: Exception) {
            error = "Le serveur est injoignable."; false
        }
    }

    Column(modifier = Modifier.padding(TeddSpace.S2), verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
        if (!session.connected) {
            // Le réglage vit sur le serveur : sans compte serveur, rien à régler ici.
            Row(verticalAlignment = Alignment.Top, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                TeddIcon(name = "alert", size = 16.dp, color = TeddTokens.Laterite)
                Text(
                    "Le modèle se règle sur le serveur : connecte d'abord cet appareil à ton compte serveur, dans la section Serveur ci-dessus. Le formulaire apparaîtra ici.",
                    color = TeddTokens.InkMuted, style = TeddType.Caption,
                )
            }
            return@Column
        }
        if (!loaded) {
            Text("Lecture du réglage…", color = TeddTokens.InkMuted, style = TeddType.Caption)
            return@Column
        }
        current?.let { c ->
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
                TeddIcon(name = "check", size = 18.dp, color = TeddTokens.Success, strokeWidth = 2.dp)
                Column(modifier = Modifier.weight(1f)) {
                    Text(c.name, color = TeddTokens.Ink, style = TeddType.Label)
                    Text(c.url + if (c.has_key) " · clé enregistrée" else " · sans clé", color = TeddTokens.InkMuted, style = TeddType.MonoSm)
                }
            }
        } ?: Row(verticalAlignment = Alignment.Top, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            TeddIcon(name = "alert", size = 16.dp, color = TeddTokens.Laterite)
            Text(
                "Aucun modèle : l'assistant ne peut pas répondre. Le serveur de teddApp n'en héberge pas ; choisis un service en ligne avec ta clé, ou ton propre serveur joignable par une adresse publique.",
                color = TeddTokens.InkMuted, style = TeddType.Caption,
            )
        }
        FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
            AssistantModelForm.services.forEach { s ->
                TeddFilterChip(label = s.label, selected = s.id == service.id, onClick = { choose(s) })
            }
        }
        TeddField(
            label = "Adresse de l'API",
            value = url,
            onValueChange = { url = it; status = null },
            keyboardType = KeyboardType.Uri,
            placeholder = if (service.id == "llama") "https://mon-serveur.exemple:9931/v1" else "https://…/v1",
        )
        TeddField(
            label = "Modèle",
            value = name,
            onValueChange = { name = it; status = null },
            placeholder = service.example.ifEmpty { "nom du modèle chez ce service" },
        )
        TeddField(
            label = if (service.needsKey) "Clé d'API" else "Clé d'API, si le serveur en demande une",
            value = key,
            onValueChange = { key = it; status = null },
            keyboardType = KeyboardType.Password,
            visualTransformation = teddPasswordTransformation(),
            placeholder = if (current?.has_key == true) "clé enregistrée, laisser vide pour la garder" else "jamais affichée après l'enregistrement",
        )
        TeddField(
            label = "Fenêtre de contexte, en tokens (le --ctx-size du serveur, par place)",
            value = window,
            onValueChange = { window = it.filter(Char::isDigit); status = null },
            keyboardType = KeyboardType.Number,
        )
        Text(
            if (AssistantModelForm.looksLocal(url)) {
                "Cette adresse désigne cet appareil ou ton réseau local : le serveur ne la joindra que s'il l'a mise dans ses hôtes admis. Pour un vrai usage, il faut une adresse publique ou un service en ligne."
            } else {
                "La clé est chiffrée sur le serveur et ne revient jamais ici."
            },
            color = if (AssistantModelForm.looksLocal(url)) TeddTokens.Laterite else TeddTokens.InkMuted, style = TeddType.Caption,
        )
        error?.let { Text(it, color = TeddTokens.Laterite, style = TeddType.Caption) }
        status?.let { Text(it, color = TeddTokens.Ink, style = TeddType.Caption) }
        Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2), verticalAlignment = Alignment.CenterVertically) {
            TeddButton(
                text = if (busy) "…" else "Enregistrer",
                onClick = { busy = true; scope.launch { if (save()) status = "Réglage enregistré."; busy = false } },
                kind = TeddButtonKind.Primary,
                enabled = !busy,
            )
            TeddButton(
                text = "Tester",
                onClick = {
                    busy = true
                    scope.launch {
                        if (save()) {
                            status = "Test en cours…"
                            status = try {
                                val r = session.authed { api, access -> api.testAssistantModel(access) }
                                AssistantModelForm.testMessage(r.ok, r.latency_ms, r.code)
                            } catch (e: ServerException) {
                                AssistantModelForm.testMessage(false, null, e.code)
                            } catch (e: Exception) {
                                "Le serveur est injoignable."
                            }
                        }
                        busy = false
                    }
                },
                kind = TeddButtonKind.Secondary,
                enabled = !busy,
                icon = "play",
            )
            if (current != null) {
                TeddButton(
                    text = "Retirer",
                    onClick = {
                        busy = true
                        scope.launch {
                            try {
                                session.authed { api, access -> api.deleteAssistantModel(access) }
                                current = null; key = ""; status = "Réglage retiré : l'assistant ne répondra plus."; error = null
                            } catch (e: ServerException) {
                                error = serverMessage(e)
                            } catch (e: Exception) {
                                error = "Le serveur est injoignable."
                            }
                            busy = false
                        }
                    },
                    kind = TeddButtonKind.Secondary,
                    size = TeddButtonSize.Md,
                    enabled = !busy,
                )
            }
        }
    }
}
